import { callBackend } from '../utils/api';

const ROOT = 'api/watchlist/attribute-ai';

const SAFE_MESSAGES = {
    ACCESS_VERIFICATION_UNAVAILABLE: 'The app could not verify application access because the service is temporarily unavailable. Your saved AI proposal is retained; reload it when the connection is restored.',
    attribute_ai_disabled: 'AI attribute creation is not currently enabled.',
    attribute_ai_provider_credential_rejected: 'The AI provider rejected your API key. Update it in AI settings, then reload saved progress before explicitly resuming.',
    attribute_ai_credential_missing: 'Add your provider API key in AI settings before starting research.',
    attribute_ai_provider_limited: 'The AI provider reached a billing or rate limit. Check your provider account before explicitly resuming.',
    attribute_ai_provider_timeout: 'The AI provider timed out. Billing may already have occurred. Reload saved progress before explicitly retrying.',
    attribute_ai_response_invalid: 'The AI provider returned an invalid classification response. Saved results are retained; reload before explicitly retrying.',
    attribute_ai_provider_incomplete: 'The AI provider stopped before completing this batch. Saved progress is retained; reload before retrying.',
    attribute_ai_provider_refused: 'The AI provider declined this classification request. Saved progress is retained; review the instructions before retrying.',
    attribute_ai_model_unsupported: 'This model does not support attribute classification. Choose a supported model in AI settings.',
    attribute_ai_context_changed: 'Your Watchlist changed. Start a new proposal against the current Watchlist.',
    attribute_ai_watchlist_missing: 'Load an accepted Watchlist before creating an AI attribute.',
    attribute_ai_pending_draft: 'Finish the pending Watchlist review before applying this attribute. Your proposal is saved.',
    attribute_ai_version_changed: 'This proposal changed. Reload saved progress before continuing.',
    attribute_ai_not_found: 'The saved proposal is unavailable. Start a new proposal.',
    attribute_ai_not_editable: 'This proposal is no longer editable. Reload its status or start a new proposal.',
    attribute_ai_review_required: 'Finish reviewing the remaining securities before applying this attribute.',
    attribute_ai_no_blank_values: 'This attribute has no blank values to fill. Existing assignments were not changed.',
    attribute_ai_recipe_not_found: 'The saved prompt is unavailable for this attribute. Reload the attribute editor.',
    attribute_ai_no_assignments: 'There are no reviewed assignments to apply. Existing values were not changed.',
};

// Keep provider/request bodies out of error copy: validation and SDK errors may
// contain submitted credentials. Status/code alone select user-facing messages.
export class WatchlistAttributeAiError extends Error {
    constructor(status, code) {
        const message = SAFE_MESSAGES[code] || (status === 409
            ? 'This proposal or Watchlist has changed. Reload the proposal before continuing.'
            : status === 401 || status === 403
                ? 'Authorization was rejected. Check your application session and AI credentials.'
                : status === 429
                    ? 'The research limit has been reached. Review saved progress before trying again.'
                    : status === 422 || status === 400
                        ? 'The request could not be accepted. Check the attribute and category rules.'
                        : status === 503
                            ? 'The service is temporarily unavailable. Your saved AI proposal is retained; reload it before trying again.'
                            : status === 504
                                ? 'Research timed out. Saved progress is retained; reload before explicitly retrying.'
                                : 'The request could not be completed. Saved progress is retained; reload before trying again.');
        super(message);
        this.name = 'WatchlistAttributeAiError';
        this.status = status || null;
        this.code = /^[a-zA-Z0-9_]{1,80}$/.test(code || '') ? code : null;
    }
}

async function request(path, method = 'GET', payload = null) {
    try {
        return await callBackend(`${ROOT}${path}`, method, payload);
    } catch (error) {
        throw new WatchlistAttributeAiError(error?.status, error?.code);
    }
}

function proposalPath(id) {
    if (typeof id !== 'string' || !id.trim()) throw new Error('A proposal is required.');
    return `/proposals/${encodeURIComponent(id.trim())}`;
}

export const fetchAttributeAiCapabilities = () => request('/capabilities');
export const fetchAttributeAiRecipes = () => request('/recipes');
export const createAttributeAiFillBlanksProposal = (attributeId, payload) => request(
    `/attributes/${encodeURIComponent(attributeId)}/fill-blanks`, 'POST', payload,
);
export const createAttributeAiProposal = (payload) => request('/proposals', 'POST', payload);
export const fetchAttributeAiProposal = (id) => request(proposalPath(id));
export const updateAttributeAiProposal = (id, payload) => request(proposalPath(id), 'PATCH', payload);
export const generateAttributeAiBatch = (id, payload) => request(`${proposalPath(id)}/generate`, 'POST', payload);
export const cancelAttributeAiProposal = (id, expectedVersion) => request(`${proposalPath(id)}/cancel`, 'POST', {
    expected_version: expectedVersion,
});
export const applyAttributeAiProposal = (id, payload) => request(`${proposalPath(id)}/apply`, 'POST', payload);

const RESUME_KEY = 'alphamizer:attribute-ai-proposal:v1:';
const resumeKey = (userId, attributeId) => `${RESUME_KEY}${userId}${attributeId ? `:fill:${attributeId}` : ''}`;

// Persist only an opaque proposal ID, scoped by authenticated user. All content
// and review state remain server-owned; credentials use llmCredentials only.
export function readAttributeAiResumeId(userId, attributeId = '') {
    if (!userId) return null;
    try { return window.localStorage.getItem(resumeKey(userId, attributeId)) || null; } catch { return null; }
}

export function saveAttributeAiResumeId(userId, proposalId, attributeId = '') {
    if (!userId) return;
    try {
        if (proposalId) window.localStorage.setItem(resumeKey(userId, attributeId), proposalId);
        else window.localStorage.removeItem(resumeKey(userId, attributeId));
    } catch { /* Saved server progress remains available if storage is blocked. */ }
}
